import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FaLock, FaUser } from "react-icons/fa";
import favicon from "../assets/favicon.png";
import { hospitalPost } from "../utils/hospitalApi";

const HospitalLogin = () => {
  const navigate = useNavigate();
  const [values, setValues] = useState({ hospital_code: "", hospital_password: "" });
  const [loading, setLoading] = useState(false);
  const submitLockRef = useRef(false);

  useEffect(() => {
    if (localStorage.getItem("hospital_user_id")) navigate("/dashboard");
  }, [navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (submitLockRef.current) return;
    if (!values.hospital_code || !values.hospital_password) {
      toast.warning("Hospital code and password are required");
      return;
    }
    submitLockRef.current = true;
    setLoading(true);
    try {
      const res = await hospitalPost("hospital-login-verify-api", values);
      if (res.data.status === 1) {
        localStorage.setItem("hospital_user_id", res.data.hospital.hospital_user_id);
        localStorage.setItem("hospital_user", JSON.stringify(res.data.hospital));
        toast.success("Login successful");
        navigate("/dashboard", { replace: true });
        window.location.replace(`${window.location.origin}/dashboard`);
      } else {
        toast.error(res.data.msg || "Invalid login details");
      }
    } finally {
      submitLockRef.current = false;
      setLoading(false);
    }
  };

  return (
    <div className="login-wrapper">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&display=swap');
        .login-wrapper { min-height:100vh; display:flex; align-items:center; justify-content:center; background:#f1f5f9; font-family:'Nunito','DM Sans',sans-serif; padding:28px; position:relative; overflow:hidden; }
        .login-wrapper:before { content:''; position:absolute; inset:0; background:radial-gradient(circle at 18% 18%, rgba(255,255,255,.95), transparent 34%), radial-gradient(circle at 85% 12%, rgba(226,232,240,.9), transparent 28%); pointer-events:none; }
        .login-card { width:100%; max-width:440px; min-height:568px; background:rgba(255,255,255,.94); border-radius:24px; box-shadow:0 22px 54px rgba(15,23,42,.10); padding:48px 40px 34px; position:relative; z-index:1; display:flex; flex-direction:column; justify-content:center; }
        .login-header { text-align:center; margin-bottom:34px; }
        .login-logo { width:84px; height:84px; object-fit:contain; border:2px solid #fed7aa; border-radius:22px; padding:17px; background:#fff; margin-bottom:20px; }
      .login-title { margin:0; color:#111827; font-size:29px; line-height:1.08; font-weight:900; letter-spacing:2px; }
        .login-subtitle { color:#64748b; font-size:13px; font-weight:800; letter-spacing:.8px; margin-top:8px; text-transform:uppercase; }
        .login-form-group { margin-bottom:24px; }
        .login-label { display:block; color:#475569; font-size:12px; font-weight:900; letter-spacing:.9px; margin-bottom:9px; text-transform:uppercase; }
        .login-input-wrapper { position:relative; }
        .login-icon { position:absolute; left:17px; top:50%; transform:translateY(-50%); color:#94a3b8; font-size:15px; z-index:2; }
        .login-input { width:100%; height:36px; border:1px solid #dbe3ef; background:#eef3f9; border-radius:12px; outline:none; padding:0 16px 0 44px; color:#334155; font-size:15px; font-weight:800; transition:.18s ease; }
        .login-input::placeholder { color:#94a3b8; font-weight:800; }
        .login-input:focus { background:#fff; border-color:#f59e0b; box-shadow:0 0 0 4px rgba(245,158,11,.12); }
        .login-input:focus ~ .login-icon { color:#f97316; }
        .login-btn { width:100%; height:46px; border:0; border-radius:13px; margin-top:12px; background:linear-gradient(135deg,#f59e0b 0%,#f97316 48%,#ea580c 100%); color:#fff; font-size:16px; font-weight:900; letter-spacing:.7px; box-shadow:0 14px 30px rgba(249,115,22,.24); transition:.18s ease; }
        .login-btn:hover { transform:translateY(-1px); box-shadow:0 17px 34px rgba(249,115,22,.30); }
        .login-btn:disabled { opacity:.72; cursor:not-allowed; transform:none; }
        .login-btn { display:flex; align-items:center; justify-content:center; gap:8px; }
        .login-spinner { width:16px; height:16px; border:2px solid rgba(255,255,255,.5); border-top-color:#fff; border-radius:50%; animation:login-spin .7s linear infinite; }
        @keyframes login-spin { to { transform:rotate(360deg); } }
        .login-footer { text-align:center; color:#64748b; font-size:12px; font-weight:800; margin-top:24px; }
        @media (max-width:520px) { .login-wrapper { padding:14px; } .login-card { min-height:auto; padding:34px 24px 28px; border-radius:20px; } .login-title { font-size:25px; } }
      `}</style>
      <form className="login-card" onSubmit={handleSubmit}>
        <div className="login-header">
          <img src={favicon} className="login-logo" alt="Satkartar Logo" />
          <h4 className="login-title">HOSPITAL PANEL</h4>
          <div className="login-subtitle">Secure Access Authentication</div>
        </div>
        <div className="login-form-group">
          <label className="login-label">Hospital Code</label>
          <div className="login-input-wrapper">
            <input className="login-input" value={values.hospital_code} onChange={(e) => setValues((p) => ({ ...p, hospital_code: e.target.value }))} placeholder="Enter your Hospital Code" />
            <FaUser className="login-icon" />
          </div>
        </div>
        <div className="login-form-group">
          <label className="login-label">Password</label>
          <div className="login-input-wrapper">
            <input type="password" className="login-input" value={values.hospital_password} onChange={(e) => setValues((p) => ({ ...p, hospital_password: e.target.value }))} placeholder="Enter your Password" />
            <FaLock className="login-icon" />
          </div>
        </div>
        <button disabled={loading} type="submit" className="login-btn" aria-busy={loading}>{loading ? <><span className="login-spinner" aria-hidden="true" /> PLEASE WAIT...</> : "SECURE LOGIN"}</button>
        <div className="login-footer">&copy; 2026 Satkartar Life Limited.</div>
      </form>
    </div>
  );
};

export default HospitalLogin;
